import { Recipe } from '@/types/api';

/**
 * Generate intelligent placeholder images for recipes
 */
export class ImageUtils {
  /**
   * Generate Unsplash URL based on recipe content
   */
  static generatePlaceholderImage(
    recipeName: string,
    ingredients: Array<{name: string}> = [],
    tags: Array<{tag: string}> = []
  ): string {
    // Extraer ingrediente principal o palabra clave
    const keywords = this.extractKeywords(recipeName, ingredients, tags);
    const searchTerm = keywords.join(',');
    
    // Usar Unsplash Source (sin key, pero funcional)
    return `https://source.unsplash.com/400x300/?${encodeURIComponent(searchTerm)},food,recipe`;
  }

  /**
   * Get optimized image URL for recipe
   */
  static getRecipeImageUrl(recipe: Recipe): string {
    // Si ya tiene imagen, usarla
    if (recipe.imageUrl) {
      return recipe.imageUrl;
    }

    // Generar placeholder inteligente
    return this.generatePlaceholderImage(
      recipe.title,
      recipe.ingredients || [],
      recipe.tags || []
    );
  }

  /**
   * Extract relevant keywords from recipe data
   */
  private static extractKeywords(
    recipeName: string,
    ingredients: Array<{name: string}> = [],
    tags: Array<{tag: string}> = []
  ): string[] {
    const keywords: string[] = [];
    
    // Mapeo de ingredientes en español a inglés para Unsplash
    const ingredientMap: Record<string, string> = {
      'pollo': 'chicken',
      'carne': 'beef', 
      'cerdo': 'pork',
      'pescado': 'fish',
      'salmón': 'salmon',
      'pasta': 'pasta',
      'arroz': 'rice',
      'ensalada': 'salad',
      'sopa': 'soup',
      'pizza': 'pizza',
      'pan': 'bread',
      'queso': 'cheese',
      'huevo': 'egg',
      'tomate': 'tomato',
      'verduras': 'vegetables',
      'frutas': 'fruits',
      'chocolate': 'chocolate',
      'café': 'coffee',
      'té': 'tea',
      'curry': 'curry',
      'taco': 'tacos',
      'burger': 'burger',
      'cake': 'cake',
      'pastel': 'cake',
    };

    // Extraer de ingredientes principales (primeros 2)
    ingredients.slice(0, 2).forEach(ingredient => {
      const name = ingredient.name.toLowerCase();
      const englishIngredient = ingredientMap[name] || name;
      if (!keywords.includes(englishIngredient)) {
        keywords.push(englishIngredient);
      }
    });

    // Extraer de tags
    tags.forEach(tag => {
      const tagName = tag.tag.toLowerCase();
      const englishTag = ingredientMap[tagName] || tagName;
      if (!keywords.includes(englishTag)) {
        keywords.push(englishTag);
      }
    });

    // Extraer del nombre de la receta
    const nameKeywords = this.extractFromRecipeName(recipeName);
    nameKeywords.forEach(keyword => {
      if (!keywords.includes(keyword)) {
        keywords.push(keyword);
      }
    });

    // Fallback si no hay keywords específicas
    if (keywords.length === 0) {
      keywords.push('food', 'recipe');
    }

    // Limitar a 3 keywords para URL más limpia
    return keywords.slice(0, 3);
  }

  /**
   * Extract keywords from recipe name
   */
  private static extractFromRecipeName(name: string): string[] {
    const keywords: string[] = [];
    const nameLower = name.toLowerCase();

    // Detectar tipos de plato
    if (nameLower.includes('sopa') || nameLower.includes('soup')) keywords.push('soup');
    if (nameLower.includes('ensalada') || nameLower.includes('salad')) keywords.push('salad');
    if (nameLower.includes('pasta')) keywords.push('pasta');
    if (nameLower.includes('pizza')) keywords.push('pizza');
    if (nameLower.includes('curry')) keywords.push('curry');
    if (nameLower.includes('taco')) keywords.push('tacos');
    if (nameLower.includes('burger')) keywords.push('burger');
    if (nameLower.includes('cake') || nameLower.includes('pastel')) keywords.push('cake');
    if (nameLower.includes('bread') || nameLower.includes('pan')) keywords.push('bread');
    if (nameLower.includes('chocolate')) keywords.push('chocolate');
    if (nameLower.includes('pollo') || nameLower.includes('chicken')) keywords.push('chicken');
    if (nameLower.includes('carne') || nameLower.includes('beef')) keywords.push('beef');

    // Detectar técnicas de cocina
    if (nameLower.includes('grilled') || nameLower.includes('asado')) keywords.push('grilled');
    if (nameLower.includes('fried') || nameLower.includes('frito')) keywords.push('fried');
    if (nameLower.includes('baked') || nameLower.includes('horneado')) keywords.push('baked');

    return keywords;
  }

  /**
   * Generate fallback image URL for failed images
   */
  static getFallbackImage(): string {
    return 'https://source.unsplash.com/400x300/?food,recipe,cooking';
  }

  /**
   * Handle image error with intelligent fallback
   */
  static handleImageError(
    _recipe: Recipe,
    onError: (fallbackUrl: string) => void
  ): void {
    // Intentar con placeholder más genérico
    const fallbackUrl = this.getFallbackImage();
    onError(fallbackUrl);
  }
}
